import {keys} from '../../../constants/KeyCodes';
import {closeFormBySession} from '../../Form/actions/form';
import {zoom} from '../../Form/actions/zoom';
import {SuspendedJobTypes} from '../../Form/consts/suspendedJobTypes';
import {Column, Form, formsStore} from '../../../stores/Form';
import {fieldValue} from '../../../stores/Form/Field';

export type FormatEventForMacroProps = {
    e: KeyboardEvent | MouseEvent;
    rowIdx?: number;
    colIdx?: number;
    formName?: string;
    action?: string;
    value?: fieldValue;
    column?: Column;
    formLevel?: number;
    parentForm?: Form;
    session?: string;
};

export type FormattedKeyboardEvent = {
    type: string;
    code?: string;
    metaKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
    ctrlKey?: boolean;
    rowIdx?: number;
    colIdx?: number;
    formName?: string;
    action?: string;
    value?: fieldValue;
    column?: Column;
    formLevel?: number;
    parentForm?: Form;
    session?: string;
    key?: string;
    keyCode?: number;
};

export const formatEventForMacro = ({
    e,
    rowIdx,
    colIdx,
    formName,
    action,
    value,
    column,
    formLevel,
    parentForm
}: FormatEventForMacroProps): FormattedKeyboardEvent => {
    if (e?.type === SuspendedJobTypes.Keydown || e?.type === SuspendedJobTypes.Keypress) {
        const keyboardEvent = e as KeyboardEvent;
        return {
            type: keyboardEvent.type,
            code: keyboardEvent.code,
            metaKey: keyboardEvent.metaKey,
            shiftKey: keyboardEvent.shiftKey,
            altKey: keyboardEvent.altKey,
            ctrlKey: keyboardEvent.ctrlKey,
            key: keyboardEvent.key,
            keyCode: keyboardEvent.keyCode,
            value,
            column,
            formLevel,
            parentForm
        };
    } else {
        const mouseEvent = e as MouseEvent;
        return {
            type: mouseEvent?.type,
            rowIdx,
            colIdx,
            formName,
            action,
            value,
            column,
            formLevel,
            parentForm
        };
    }
};

export async function runMacroEvents(macroEventArray: Array<FormattedKeyboardEvent>) {
    if (macroEventArray.length === 0) return;
    for await (const item of macroEventArray) {
        const activeForm = formsStore.getActiveForm();
        switch (item.type) {
            case SuspendedJobTypes.Keydown:
            case SuspendedJobTypes.Keypress: {
                const event = new KeyboardEvent(item.type, {
                    code: item?.code,
                    metaKey: item?.metaKey,
                    shiftKey: item?.shiftKey,
                    altKey: item?.altKey,
                    ctrlKey: item?.ctrlKey,
                    key: item?.key,
                    bubbles: true,
                    cancelable: false
                });
                if (Object.keys(keys).find(key => keys[key] === item.key)) {
                    await window.dispatchEvent(event);
                } else {
                    await activeForm.formActiveElement.dispatchEvent(event);
                }
                break;
            }
            case SuspendedJobTypes.Click: {
                const event = new MouseEvent(item.type, {
                    bubbles: true,
                    cancelable: false
                });

                await activeForm.formActiveElement.dispatchEvent(event);
                break;
            }
            case SuspendedJobTypes.FormGoto: {
                const currentForm = formsStore.getActiveForm();
                currentForm.setCurrentColumnIdByIndex(item.colIdx);
                currentForm.setCurrentRow(item.rowIdx);
                break;
            }
            case SuspendedJobTypes.ZoomFormExit: {
                if (item.formLevel) {
                    await formsStore.currentScreen?.handleFormLevel(item.formLevel);
                } else {
                    await closeFormBySession(formsStore.currentScreen.session, false);
                }
                break;
            }
            case SuspendedJobTypes.SubForm: {
                await formsStore.currentScreen.runSubForm(item.formName, item.parentForm);
                break;
            }
            case SuspendedJobTypes.FieldValue: {
                const activeForm = formsStore.getActiveForm();
                const activeField = activeForm.activeField();
                activeField.setCurrentValue(item.value);
                activeForm.setCurrentColumnId(activeForm.currentColumnId + 1);

                break;
            }
            case SuspendedJobTypes.Zoom: {
                await zoom(item.column.parentForm, item.column);
                break;
            }
            default: {
                return;
            }
        }
    }
}
